<template>
  <div class="customers-page">
    <!-- 顶部搜索/筛选栏 -->
    <div class="toolbar">
      <el-row :gutter="12" align="middle">
        <el-col :xs="24" :sm="8" :md="6">
          <el-input v-model="searchKeyword" placeholder="搜索公司名称/行业/国家" clearable prefix-icon="Search"
            @keyup.enter="handleSearch" @clear="handleSearch" />
        </el-col>
        <el-col :xs="12" :sm="4" :md="3">
          <el-select v-model="filterCountry" placeholder="国家" clearable filterable @change="handleSearch">
            <el-option v-for="c in countryList" :key="c" :label="c" :value="c" />
          </el-select>
        </el-col>
        <el-col :xs="12" :sm="4" :md="3">
          <el-select v-model="filterLevel" placeholder="等级" clearable @change="handleSearch">
            <el-option label="A-重点" value="A" />
            <el-option label="B-一般" value="B" />
            <el-option label="C-待开发" value="C" />
          </el-select>
        </el-col>
        <el-col :xs="12" :sm="4" :md="3">
          <el-select v-model="filterOwner" placeholder="跟进人" clearable filterable @change="handleSearch">
            <el-option v-for="u in userList" :key="u.uid" :label="u.uname" :value="u.uid" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="8" :md="6" style="text-align:right">
          <el-button v-if="isAdmin" type="warning" icon="User" :disabled="!multipleSelection.length"
            @click="assignDialogVisible = true">批量分配</el-button>
          <el-button type="primary" icon="Plus" @click="showCreateDrawer">新增客户</el-button>
          <el-button icon="Download" @click="exportCustomers">导出</el-button>
        </el-col>
      </el-row>
    </div>

    <!-- 客户表格（桌面端） -->
    <el-table ref="customerTableRef" v-if="!isMobile" :data="customerList" stripe border style="width:100%" @sort-change="sortChange"
      :default-sort="{ prop: 'createdate', order: 'descending' }" row-key="companyid"
      @row-dblclick="showDetailDrawer" @selection-change="handleSelectionChange">
      <el-table-column type="selection" width="50" reserve-selection />
      <el-table-column prop="companyname" label="客户名称" min-width="220" sortable="custom" show-overflow-tooltip>
        <template #default="{ row }">
          <div class="company-cell">
            <el-tag :type="getButtonType(row.class)" size="small" class="level-tag">{{ row.class || 'C' }}</el-tag>
            <el-tag v-if="row.tracktype === 1" type="danger" size="small" class="level-tag">无效</el-tag>
            <el-tag v-else-if="row.tracktype === 2" type="warning" size="small" class="level-tag">长线</el-tag>
            <span class="company-name" @click.stop="showDetailDrawer(row)">{{ row.companyname }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="所属公司" min-width="140" show-overflow-tooltip>
        <template #default="{ row }">
          <span>{{ row.sellercompany || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="country" label="国家" width="120" sortable="custom" />
      <el-table-column prop="industry" label="行业/关键字" min-width="140" show-overflow-tooltip />
      <el-table-column label="联系人" width="120" show-overflow-tooltip>
        <template #default="{ row }">
          <span>{{ row.primarycontact || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="username" label="跟进人" width="100" sortable="custom" />
      <el-table-column label="最近联系" width="110" sortable="custom" prop="lastcontactdate">
        <template #default="{ row }">
          <span :class="{ 'text-danger': isOverdue(row.lastcontactdate, 30) }">
            {{ formatDate(row.lastcontactdate) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="createdate" label="创建日期" width="110" sortable="custom">
        <template #default="{ row }">
          {{ formatDate(row.createdate) }}
        </template>
      </el-table-column>
      <el-table-column label="商机" width="70" align="center">
        <template #default="{ row }">
          <el-button v-if="row.opportunitycount > 0" type="primary" link size="small"
            @click.stop="goToOpportunities(row)">
            {{ row.opportunitycount }}
          </el-button>
          <span v-else class="text-muted">0</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="140" fixed="right">
        <template #default="{ row }">
          <el-button type="primary" link icon="Edit" @click.stop="showEditDrawer(row)" title="编辑" />
          <el-popconfirm title="确定删除此客户？" @confirm="deleteCustomer(row)">
            <template #reference>
              <el-button type="danger" link icon="Delete" title="删除" />
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>

    <!-- 移动端卡片视图 -->
    <div v-if="isMobile" class="mobile-cards">
      <div v-for="row in customerList" :key="row.companyid" class="customer-card"
        @click="showDetailDrawer(row)">
        <div class="card-header">
          <el-tag :type="getButtonType(row.class)" size="small">{{ row.class || 'C' }}</el-tag>
          <el-tag v-if="row.tracktype === 1" type="danger" size="small">无效</el-tag>
          <el-tag v-else-if="row.tracktype === 2" type="warning" size="small">长线</el-tag>
          <span class="card-company-name">{{ row.companyname }}</span>
          <span class="card-country">{{ row.country }}</span>
        </div>
        <div class="card-body">
          <div class="card-field">
            <span class="field-label">行业</span>
            <span class="field-value">{{ row.industry || '-' }}</span>
          </div>
          <div class="card-field">
            <span class="field-label">联系人</span>
            <span class="field-value">{{ row.primarycontact || '-' }}</span>
          </div>
          <div class="card-field">
            <span class="field-label">所属公司</span>
            <span class="field-value">{{ row.sellercompany || '-' }}</span>
          </div>
          <div class="card-field">
            <span class="field-label">跟进人</span>
            <span class="field-value">{{ row.username || '-' }}</span>
          </div>
          <div class="card-field">
            <span class="field-label">最近联系</span>
            <span class="field-value" :class="{ 'text-danger': isOverdue(row.lastcontactdate, 30) }">
              {{ formatDate(row.lastcontactdate) }}
            </span>
          </div>
          <div class="card-field">
            <span class="field-label">创建日期</span>
            <span class="field-value">{{ formatDate(row.createdate) }}</span>
          </div>
        </div>
        <div class="card-footer">
          <span class="card-opportunity">
            商机 <b>{{ row.opportunitycount || 0 }}</b>
          </span>
          <span class="card-actions" @click.stop>
            <el-button type="primary" link size="small" icon="Edit" @click="showEditDrawer(row)" />
            <el-popconfirm title="确定删除此客户？" @confirm="deleteCustomer(row)">
              <template #reference>
                <el-button type="danger" link size="small" icon="Delete" />
              </template>
            </el-popconfirm>
          </span>
        </div>
      </div>
      <el-empty v-if="!customerList.length" description="暂无客户" />
    </div>

    <!-- 分页 -->
    <div class="pagination-wrap">
      <el-pagination v-model:current-page="currentPage" v-model:page-size="pageSize"
        :page-sizes="[20, 50, 100]" :total="total" layout="total, sizes, prev, pager, next, jumper"
        background @size-change="fetchCustomers(true)" @current-change="fetchCustomers()" />
    </div>

    <!-- 新增/编辑客户抽屉 -->
    <el-drawer v-model="drawerVisible" :title="isEdit ? '编辑客户' : '新增客户'" size="680px" :destroy-on-close="true">
      <el-form ref="customerFormRef" :model="customerForm" :rules="customerRules" label-width="100px" size="default">
        <el-divider content-position="left">基本信息</el-divider>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="公司名称" prop="companyname">
              <el-input v-model="customerForm.companyname" placeholder="公司全名" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="等级" prop="class">
              <el-select v-model="customerForm.class" placeholder="客户等级">
                <el-option label="A-重点客户" value="A" />
                <el-option label="B-一般客户" value="B" />
                <el-option label="C-待开发" value="C" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="所属公司">
              <el-select v-model="customerForm.profileid" placeholder="选择所属公司" clearable filterable>
                <el-option v-for="p in profileList" :key="p.id" :label="profileLabel(p)" :value="p.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="跟踪性质">
              <el-select v-model="customerForm.tracktype" placeholder="选择跟踪性质">
                <el-option label="正常跟进" :value="0" />
                <el-option label="无效客户" :value="1" />
                <el-option label="长线跟踪" :value="2" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="国家/地区" prop="country">
              <el-select v-model="customerForm.country" filterable allow-create placeholder="选择或输入">
                <el-option v-for="c in countryList" :key="c" :label="c" :value="c" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="城市">
              <el-input v-model="customerForm.city" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="行业/关键字" prop="industry">
              <el-input v-model="customerForm.industry" placeholder="如：电力、继电保护" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="跟进人" prop="ownerid">
              <el-select v-model="customerForm.ownerid" placeholder="分配跟进人">
                <el-option v-for="u in userList" :key="u.uid" :label="u.uname" :value="u.uid" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="详细地址">
          <el-input v-model="customerForm.address" />
        </el-form-item>
        <el-divider content-position="left">联系方式</el-divider>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="电话">
              <el-input v-model="customerForm.phone" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="邮箱">
              <el-input v-model="customerForm.email" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="网站">
              <el-input v-model="customerForm.website" placeholder="https://" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Facebook">
              <el-input v-model="customerForm.facebook" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="LinkedIn">
              <el-input v-model="customerForm.linkedin" />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 联系人列表（编辑模式） -->
        <template v-if="isEdit">
          <el-divider content-position="left">
            联系人
            <el-button type="primary" link icon="Plus" @click="addContact">添加</el-button>
          </el-divider>
          <div v-for="(ct, idx) in customerForm.contacts" :key="idx" class="contact-row">
            <el-row :gutter="8" align="middle">
              <el-col :span="4"><el-input v-model="ct.fullname" placeholder="姓名" size="small" /></el-col>
              <el-col :span="3"><el-input v-model="ct.phone" placeholder="电话" size="small" /></el-col>
              <el-col :span="4"><el-input v-model="ct.email" placeholder="邮箱" size="small" /></el-col>
              <el-col :span="3"><el-input v-model="ct.position" placeholder="职位" size="small" /></el-col>
              <el-col :span="3"><el-input v-model="ct.whatsapp" placeholder="WhatsApp" size="small" /></el-col>
              <el-col :span="3"><el-input v-model="ct.skype" placeholder="Skype" size="small" /></el-col>
              <el-col :span="2"><el-button type="danger" link icon="Delete" @click="customerForm.contacts.splice(idx,1)" /></el-col>
            </el-row>
          </div>
        </template>
      </el-form>
      <template #footer>
        <el-button @click="drawerVisible = false">取消</el-button>
        <el-button type="primary" @click="saveCustomer" :loading="saving">保存</el-button>
      </template>
    </el-drawer>

    <!-- 客户详情抽屉 -->
    <el-drawer v-model="detailVisible" :title="detailData.companyname" size="720px" :destroy-on-close="true">
      <el-tabs v-model="detailTab">
        <!-- 概览 -->
        <el-tab-pane label="概览" name="overview">
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item label="等级">
              <el-tag :type="getButtonType(detailData.class)" size="small">{{ detailData.class || 'C' }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="跟踪性质">
              <el-tag v-if="detailData.tracktype === 1" type="danger" size="small">无效</el-tag>
              <el-tag v-else-if="detailData.tracktype === 2" type="warning" size="small">长线</el-tag>
              <span v-else>正常跟进</span>
            </el-descriptions-item>
            <el-descriptions-item label="国家">{{ detailData.country }}</el-descriptions-item>
            <el-descriptions-item label="行业">{{ detailData.industry }}</el-descriptions-item>
            <el-descriptions-item label="跟进人">{{ detailData.username }}</el-descriptions-item>
            <el-descriptions-item label="电话">{{ detailData.phone }}</el-descriptions-item>
            <el-descriptions-item label="邮箱">{{ detailData.email }}</el-descriptions-item>
            <el-descriptions-item label="网站" :span="2">{{ detailData.website }}</el-descriptions-item>
            <el-descriptions-item label="地址" :span="2">{{ detailData.address }}</el-descriptions-item>
            <el-descriptions-item label="Facebook">{{ detailData.facebook }}</el-descriptions-item>
            <el-descriptions-item label="LinkedIn">{{ detailData.linkedin }}</el-descriptions-item>
            <el-descriptions-item label="最近联系">{{ formatDate(detailData.lastcontactdate) }}</el-descriptions-item>
            <el-descriptions-item label="创建日期">{{ formatDate(detailData.createdate) }}</el-descriptions-item>
          </el-descriptions>
        </el-tab-pane>

        <!-- 联系人 -->
        <el-tab-pane label="联系人" name="contacts">
          <el-table :data="detailContacts" stripe size="small" border>
            <el-table-column prop="fullname" label="姓名" width="100" />
            <el-table-column prop="position" label="职位" width="100" />
            <el-table-column prop="phone" label="电话" width="120" />
            <el-table-column prop="email" label="邮箱" min-width="160" show-overflow-tooltip />
            <el-table-column prop="whatsapp" label="WhatsApp" width="120" />
            <el-table-column prop="skype" label="Skype" width="100" />
            <el-table-column label="操作" width="100">
              <template #default="{ row }">
                <el-button type="primary" link icon="Edit" @click="editContact(row)" />
                <el-popconfirm title="确定删除？" @confirm="deleteContact(row)">
                  <template #reference>
                    <el-button type="danger" link icon="Delete" />
                  </template>
                </el-popconfirm>
              </template>
            </el-table-column>
          </el-table>
          <el-button type="primary" icon="Plus" size="small" style="margin-top:10px" @click="addContactForDetail">
            添加联系人
          </el-button>
        </el-tab-pane>

        <!-- 关联商机 -->
        <el-tab-pane label="商机" name="opportunities">
          <el-table :data="detailOpportunities" stripe size="small" border>
            <el-table-column prop="opname" label="商机名称" min-width="180" show-overflow-tooltip />
            <el-table-column prop="opamount" label="金额" width="100" />
            <el-table-column prop="opstatus" label="状态" width="80">
              <template #default="{ row }">
                <el-tag size="small" :type="row.opstatus === 2 ? 'success' : row.opstatus === 1 ? 'warning' : 'info'">
                  {{ { 0: '无效', 1: '进行中', 2: '已成交' }[row.opstatus] || '-' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="opclass" label="优先级" width="80">
              <template #default="{ row }">
                <el-tag size="small" :type="row.opclass === 1 ? 'danger' : row.opclass === 2 ? 'warning' : ''">
                  {{ { 1: '高', 2: '中', 3: '普通' }[row.opclass] || '-' }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
          <el-empty v-if="!detailOpportunities.length" description="暂无商机" />
          <el-button type="primary" icon="Plus" size="small" @click="goToOpportunities(detailData)">创建商机</el-button>
        </el-tab-pane>

        <!-- 报价/单证 -->
        <el-tab-pane label="报价单" name="quotations">
          <el-table :data="detailQuotations" stripe size="small" border>
            <el-table-column prop="docnumber" label="单号" width="160" />
            <el-table-column prop="issuedate" label="日期" width="100" />
            <el-table-column prop="currency" label="币种" width="60" />
            <el-table-column prop="totalamount" label="金额" width="100">
              <template #default="{row}">
                {{ formatAmount(row.totalamount, row.currency) }}
              </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="80" />
          </el-table>
          <el-empty v-if="!detailQuotations.length" description="暂无报价单" />
        </el-tab-pane>
      </el-tabs>
    </el-drawer>

    <!-- 联系人编辑对话框 -->
    <el-dialog v-model="contactDialogVisible" :title="contactForm.contactid ? '编辑联系人' : '添加联系人'" width="500px" :destroy-on-close="true">
      <el-form ref="contactFormRef" :model="contactForm" label-width="90px" size="default">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="姓名" required>
              <el-input v-model="contactForm.fullname" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="职位">
              <el-input v-model="contactForm.position" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="电话">
              <el-input v-model="contactForm.phone" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="邮箱">
              <el-input v-model="contactForm.email" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="WhatsApp">
              <el-input v-model="contactForm.whatsapp" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Skype">
              <el-input v-model="contactForm.skype" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="其他联系">
          <el-input v-model="contactForm.othercontact" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="contactForm.notes" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="contactDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveContact" :loading="savingContact">保存</el-button>
      </template>
    </el-dialog>

    <!-- 批量分配跟进人 -->
    <el-dialog v-model="assignDialogVisible" title="批量分配跟进人" width="440px" :close-on-click-modal="false"
      :destroy-on-close="true">
      <el-form label-width="90px">
        <el-form-item label="已选客户">
          <span>{{ multipleSelection.length }} 个</span>
        </el-form-item>
        <el-form-item label="分配至" required>
          <el-select v-model="assignTargetUid" placeholder="选择跟进人" filterable style="width:100%">
            <el-option v-for="u in userList" :key="u.uid" :label="u.uname" :value="u.uid" />
          </el-select>
        </el-form-item>
        <el-form-item label="说明">
          <div style="line-height:1.6;color:#909399;font-size:12px">
            在途商机将随客户一并转移给新跟进人；已成交/关闭商机的历史业绩保留在原跟进人名下。
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="assignDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="assigning" @click="submitAssign">确认分配</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
    import { post, get } from '@/api/index'
    import { ElMessage } from 'element-plus'

    export default {
        name: 'CustomersPage',
        data() {
            return {
                // 列表数据
                customerList: [],
                total: 0,
                countryList: [],
                userList: [],
                // 批量分配
                isAdmin: false,
                multipleSelection: [],
                assignDialogVisible: false,
                assignTargetUid: '',
                assigning: false,
                // 所属公司（公司档案）
                profileList: [],
                defaultProfileId: '',
                // 搜索/筛选
                searchKeyword: '',
                filterCountry: '',
                filterLevel: '',
                filterOwner: '',
                filterSort: 'createdate_desc',
                // 分页
                currentPage: 1,
                pageSize: 50,
                // 抽屉
                drawerVisible: false,
                isEdit: false,
                saving: false,
                customerForm: {},
                customerRules: {
                    companyname: [{ required: true, message: '请输入公司名称', trigger: 'blur' }],
                    country: [{ required: true, message: '请选择国家', trigger: 'change' }],
                    ownerid: [{ required: true, message: '请选择跟进人', trigger: 'change' }]
                },
                // 详情抽屉
                detailVisible: false,
                detailData: {},
                detailTab: 'overview',
                detailContacts: [],
                detailOpportunities: [],
                detailQuotations: [],
                // 联系人对话框
                contactDialogVisible: false,
                savingContact: false,
                contactForm: {},
                // 响应式
                isMobile: false
            }
        },
        computed: {},
        created() {
            this.isAdmin = localStorage.getItem('userid') === '1'
            this.fetchCustomers()
            this.fetchUsers()
            this.fetchCountries()
            this.fetchProfiles()
            this.openCustomerFromRoute(this.$route.query)
        },
        watch: {
            '$route.query'(q) {
                this.openCustomerFromRoute(q)
            }
        },
        mounted() {
            this.checkMobile()
            this._resizeHandler = () => this.checkMobile()
            window.addEventListener('resize', this._resizeHandler)
        },
        beforeUnmount() {
            window.removeEventListener('resize', this._resizeHandler)
        },
        methods: {
            checkMobile() {
                this.isMobile = window.innerWidth < 768
            },
            // ASP 响应可能因前置空白字符而未被 Axios 自动解析为 JSON
            _parseRes(raw) {
                if (!raw) return raw
                if (typeof raw === 'string') {
                    try { return JSON.parse(raw.trim()) } catch (e) { return raw }
                }
                return raw
            },
            formatAmount(val, currency) {
                const n = parseFloat(val) || 0
                const symbol = { USD: '$', EUR: '€', CNY: '¥', GBP: '£' }[currency] || ''
                return symbol + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            },
            async fetchCustomers(resetPage = false) {
                if (resetPage) this.currentPage = 1
                try {
                    const params = {
                        action: 'listcustomers',
                        page: this.currentPage,
                        size: this.pageSize,
                        order: this.filterSort
                    }
                    if (this.searchKeyword) params.sea = this.searchKeyword
                    if (this.filterCountry) params.country = this.filterCountry
                    if (this.filterLevel) params.level = this.filterLevel
                    if (this.filterOwner) params.ownerid = this.filterOwner

                    const raw = await post('customersapi.asp', params)
                    const data = this._parseRes(raw)
                    // 后端返回格式: { data: [...], total: N }
                    if (data && data.data && Array.isArray(data.data)) {
                        this.customerList = data.data
                        this.total = data.total || data.data.length
                    } else if (data && Array.isArray(data)) {
                        this.customerList = data
                        this.total = data.length
                    } else if (data && data.list) {
                        this.customerList = data.list
                        this.total = data.total || data.list.length
                    } else {
                        console.warn('fetchCustomers: unexpected format', typeof data, data)
                        this.customerList = []
                        this.total = 0
                    }
                } catch (e) {
                    console.error('fetchCustomers error', e)
                }
            },
            async fetchUsers() {
                try {
                    const res = await post('system.asp', { action: 'users' })
                    this.userList = res.users
                } catch (e) { console.error(e) }
            },
            handleSelectionChange(rows) {
                this.multipleSelection = rows || []
            },
            async submitAssign() {
                if (!this.multipleSelection.length) {
                    ElMessage.warning('请先勾选客户')
                    return
                }
                if (!this.assignTargetUid) {
                    ElMessage.warning('请选择跟进人')
                    return
                }
                this.assigning = true
                try {
                    const ids = this.multipleSelection.map(r => r.companyid)
                    const res = this._parseRes(await post('customersapi.asp', {
                        action: 'assignowner',
                        companyids: ids.join(','),
                        ownerid: this.assignTargetUid
                    }))
                    if (res && res.result === 'done') {
                        const oppN = Number(res.opportunities || 0)
                        const oppTip = oppN > 0 ? `，同步转移 ${oppN} 个在途商机` : ''
                        ElMessage.success(`已分配 ${res.count || ids.length} 个客户${oppTip}`)
                        this.assignDialogVisible = false
                        this.assignTargetUid = ''
                        if (this.$refs.customerTableRef) this.$refs.customerTableRef.clearSelection()
                        this.fetchCustomers()
                    } else {
                        ElMessage.error(res?.error || '分配失败')
                    }
                } catch (e) {
                    console.error('submitAssign error', e)
                    ElMessage.error('分配失败')
                } finally {
                    this.assigning = false
                }
            },
            async fetchCountries() {
                try {
                    const res = await post('system.asp', { action: 'countries' })
                    // system.asp countries 返回 {countries: [...]}
                    const list = res.countries || []
                    this.countryList = list.map(c => c.name || c.code)
                } catch (e) { console.error(e) }
            },
            // 公司档案（所属公司）列表 — 复用 quotation.vue 的 system.asp profile 数据源
            async fetchProfiles() {
                try {
                    const res = this._parseRes(await post('system.asp', { action: 'profile', Selection: 'all' }))
                    this.profileList = res.profile || res.list || (Array.isArray(res) ? res : [])
                    const def = this.profileList.find(p => this.isDefaultProfile(p))
                    this.defaultProfileId = def ? def.id : (this.profileList.length ? this.profileList[0].id : '')
                } catch (e) { console.error(e) }
            },
            async ensureProfiles() {
                if (!this.profileList.length) await this.fetchProfiles()
            },
            isDefaultProfile(p) {
                return p && (p.default === true || p.default === 1 || p.default === '1')
            },
            profileLabel(p) {
                const name = p.companyname || p.profilename || p.companyName || `公司 ${p.id}`
                return this.isDefaultProfile(p) ? `${name}（默认）` : name
            },
            handleSearch() {
                this.fetchCustomers(true)
            },
            sortChange({ prop, order }) {
                this.filterSort = prop || 'createdate'
                if (order === 'ascending') this.filterSort += '_asc'
                else if (order === 'descending') this.filterSort += '_desc'
                this.fetchCustomers(true)
            },

            // 新增/编辑
            async showCreateDrawer() {
                await this.ensureProfiles()
                this.isEdit = false
                this.customerForm = {
                    companyname: '', country: '', industry: '', class: 'C',
                    tracktype: 0,
                    ownerid: '', city: '', address: '', phone: '', email: '',
                    website: '', facebook: '', linkedin: '',
                    profileid: this.defaultProfileId,
                    contacts: []
                }
                this.drawerVisible = true
            },
            async showEditDrawer(row) {
                await this.ensureProfiles()
                this.isEdit = true
                this.customerForm = { ...row, contacts: [] }
                // 回填 profileid（列表 row.profileid 可能与档案 id 类型不一致，按值匹配）
                if (row.profileid !== undefined && row.profileid !== null && row.profileid !== '') {
                    const match = this.profileList.find(p => String(p.id) === String(row.profileid))
                    if (match) this.customerForm.profileid = match.id
                }
                this.drawerVisible = true
                this.fetchContacts(row.companyid)
            },
            async fetchContacts(companyid) {
                try {
                    let contactsRes = await post('customersapi.asp', { action: 'getcontactlist', companyid })
                    contactsRes = this._parseRes(contactsRes)
                    this.customerForm.contacts = contactsRes || []
                } catch (e) { console.error(e) }
            },
            addContact() {
                this.customerForm.contacts.push({ fullname: '', phone: '', email: '', position: '', whatsapp: '', skype: '' })
            },
            async saveCustomer() {
                try {
                    await this.$refs.customerFormRef.validate()
                } catch { return }
                this.saving = true
                try {
                    const payload = { action: 'savecustomer', mode: this.isEdit ? 'edit' : 'add', ...this.customerForm, profileid: this.customerForm.profileid }
                    const saveRes = this._parseRes(await post('customersapi.asp', payload))
                    if (saveRes && (saveRes.result === 'created' || saveRes.result === 'updated')) {
                        // 保存联系人（如果有）
                        const savedCompanyId = saveRes.companyid || this.customerForm.companyid
                        const validContacts = (this.customerForm.contacts || []).filter(c => c.fullname)
                        if (savedCompanyId && validContacts.length > 0) {
                            try {
                                await post('customersapi.asp', {
                                    action: 'savecontacts',
                                    companyid: savedCompanyId,
                                    contacts: JSON.stringify(validContacts)
                                })
                            } catch (e) { console.error('saveContacts failed', e) }
                        }
                        ElMessage.success(this.isEdit ? '客户已更新' : '客户已创建')
                        this.drawerVisible = false
                        this.fetchCustomers()
                    } else {
                        const errMsg = saveRes?.error || saveRes?.message || '保存失败'
                        ElMessage.error(errMsg)
                    }
                } catch (e) {
                    console.error('saveCustomer error:', e)
                    ElMessage.error(e?.message || '网络错误，请重试')
                } finally {
                    this.saving = false
                }
            },
            async deleteCustomer(row) {
                try {
                    let delRes = this._parseRes(await post('customersapi.asp', { action: 'deletecustomer', companyid: row.companyid }))
                    if (delRes && delRes.result === 'deleted') {
                        ElMessage.success('已删除')
                        this.fetchCustomers()
                    }
                } catch (e) { ElMessage.error('删除失败') }
            },

            // 从路由 query 打开客户详情（Dashboard/跟踪页跳转；兼容 ?companyid= 与 ?id=）
            async openCustomerFromRoute(query) {
                const cid = query.companyid || query.id
                if (!cid) return
                const num = parseInt(cid, 10)
                if (isNaN(num)) return
                try {
                    const raw = this._parseRes(await post('customersapi.asp', { action: 'getcustomer', companyid: num }))
                    if (raw && (raw.companyid || raw.id)) {
                        this.showDetailDrawer({
                            companyid: raw.companyid || raw.id,
                            companyname: raw.companyname || '',
                            country: raw.country || '',
                            industry: raw.industry || '',
                            class: raw.class || 'C',
                            ...raw
                        })
                    }
                } catch (e) {
                    ElMessage.error('加载客户详情失败')
                }
            },

            // 详情抽屉
            async showDetailDrawer(row) {
                this.detailData = row
                this.detailVisible = true
                this.detailTab = 'overview'
                this.loadDetailData(row.companyid)
            },
            async loadDetailData(companyid) {
                // 联系人 — getcontactlist 返回裸数组
                try {
                    const contactsRes = this._parseRes(await post('customersapi.asp', { action: 'getcontactlist', companyid }))
                    this.detailContacts = contactsRes || []
                    if (this.detailContacts.length > 0) {
                        this.detailData.primaryContact = this.detailContacts[0].fullname
                    }
                } catch (e) { }
                // 关联商机 — 返回 {result, list, total}
                try {
                    const res = await post('opportunitiesapi.asp', { action: 'list', customercompanyid: companyid })
                    this.detailOpportunities = res.list || []
                } catch (e) { }
                // 报价单 — 返回 {documents: [...]}
                try {
                    const res = await post('documents.asp', { action: 'quotationlist', companyid })
                    this.detailQuotations = (res.documents || []).filter(d => d.companyid === companyid)
                } catch (e) { }
            },

            // 联系人
            addContactForDetail() {
                this.contactForm = { companyid: this.detailData.companyid, fullname: '', phone: '', email: '', position: '', whatsapp: '', skype: '', othercontact: '', notes: '' }
                this.contactDialogVisible = true
            },
            editContact(row) {
                this.contactForm = { ...row, companyid: this.detailData.companyid }
                this.contactDialogVisible = true
            },
            async deleteContact(row) {
                try {
                    const delContactRes = this._parseRes(await post('customersapi.asp', { action: 'deletecontact', contactid: row.contactid }))
                    if (delContactRes && delContactRes.result === 'deleted') {
                        ElMessage.success('已删除')
                        this.loadDetailData(this.detailData.companyid)
                    }
                } catch (e) { ElMessage.error('删除失败') }
            },
            async saveContact() {
                if (!this.contactForm.fullname) {
                    ElMessage.warning('请填写联系人姓名')
                    return
                }
                this.savingContact = true
                try {
                    const action = this.contactForm.contactid ? 'updatecontact' : 'addcontact'
                    const saveContactRes = this._parseRes(await post('customersapi.asp', { action, ...this.contactForm }))
                    if (saveContactRes && (saveContactRes.result === 'created' || saveContactRes.result === 'updated')) {
                        ElMessage.success('联系人已保存')
                        this.contactDialogVisible = false
                        this.loadDetailData(this.detailData.companyid)
                    } else {
                        ElMessage.error(saveContactRes?.error || '保存失败')
                    }
                } catch (e) {
                    ElMessage.error('保存失败')
                } finally {
                    this.savingContact = false
                }
            },

            // 导出
            async exportCustomers() {
                try {
                    const params = { action: 'listcustomers', page: 1, size: 10000, order: this.filterSort }
                    if (this.searchKeyword) params.sea = this.searchKeyword
                    if (this.filterCountry) params.country = this.filterCountry
                    if (this.filterLevel) params.level = this.filterLevel
                    if (this.filterOwner) params.ownerid = this.filterOwner
                    const exportData = this._parseRes(await post('customersapi.asp', params))
                    const list = (exportData && exportData.data) ? exportData.data : this.customerList
                    const header = '公司名称,国家,行业,等级,跟进人,最近联系,创建日期\n'
                    const rows = list.map(c =>
                        `"${c.companyname}","${c.country}","${c.industry}","${c.class}","${c.username}","${this.formatDate(c.lastcontactdate)}","${this.formatDate(c.createdate)}"`
                    ).join('\n')
                    const bom = '\uFEFF'
                    const blob = new Blob([bom + header + rows], { type: 'text/csv;charset=utf-8' })
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url; a.download = `客户列表_${new Date().toISOString().slice(0, 10)}.csv`
                    a.click(); URL.revokeObjectURL(url)
                } catch (e) {
                    ElMessage.error('导出失败')
                }
            },

            // 跳转
            goToOpportunities(row) {
                this.$router.push({ path: '/opportunities', query: { companyid: row.companyid } })
            },

            // 工具函数
            formatDate(d) {
                if (!d) return '-'
                try {
                    const dt = new Date(d)
                    if (isNaN(dt.getTime())) return d
                    return dt.toISOString().slice(0, 10)
                } catch { return d }
            },
            isOverdue(d, days) {
                if (!d) return true
                const diff = (new Date() - new Date(d)) / 86400000
                return diff > days
            },
            getNextDaysTagType(d) {
                if (!d) return 'info'
                const diff = Math.ceil((new Date(d) - new Date()) / 86400000)
                if (diff < 0) return 'danger'
                if (diff <= 3) return 'warning'
                return 'success'
            },
            getButtonType(level) {
                return level === 'A' ? 'danger' : level === 'B' ? 'warning' : 'info'
            },
            getMethodTagType(method) {
                const map = { phone: 'primary', email: 'success', whatsapp: 'success', wechat: 'success', visit: 'warning', exhibition: 'danger', other: 'info' }
                return map[method] || 'info'
            },
            getMethodText(method) {
                const map = { phone: '电话', email: '邮件', whatsapp: 'WhatsApp', wechat: '微信', visit: '拜访', exhibition: '展会', other: '其他' }
                return map[method] || method
            }
        }
    }
</script>

<style scoped>
.customers-page { padding: 16px; }
.toolbar { margin-bottom: 12px; }
.pagination-wrap { margin-top: 12px; display: flex; justify-content: flex-end; }
.company-cell { display: flex; align-items: center; gap: 6px; }
.level-tag { flex-shrink: 0; }
.company-name { cursor: pointer; color: var(--el-color-primary); }
.company-name:hover { text-decoration: underline; }
.text-danger { color: var(--el-color-danger); font-size: 12px; }
.text-muted { color: var(--el-text-color-placeholder); }
.contact-row { margin-bottom: 6px; }

/* ========== 移动端卡片视图 ========== */
.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.customer-card {
  background: #fff;
  border-radius: 8px;
  padding: 12px 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,.08);
  cursor: pointer;
  transition: box-shadow .2s;
}
.customer-card:active {
  box-shadow: 0 2px 8px rgba(0,0,0,.12);
}
.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.card-company-name {
  font-weight: 600;
  font-size: 15px;
  color: #303133;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.card-country {
  font-size: 12px;
  color: #909399;
  flex-shrink: 0;
}
.card-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 16px;
  margin-bottom: 10px;
}
.card-field {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.field-label {
  font-size: 12px;
  color: #909399;
  flex-shrink: 0;
  white-space: nowrap;
}
.field-value {
  font-size: 13px;
  color: #606266;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}
.card-opportunity {
  font-size: 12px;
  color: #909399;
}
.card-opportunity b {
  color: #409eff;
}
.card-actions {
  display: flex;
  gap: 4px;
}
</style>
